import { useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: ReactNode;
}

interface SegmentedControlProps<T extends string> {
  value: T;
  onChange: (v: T) => void;
  options: SegmentedOption<T>[];
  ariaLabel: string;
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  ariaLabel,
}: SegmentedControlProps<T>) {
  // Roving tabindex: the radiogroup is a single tab stop; ArrowLeft/Right
  // (and Up/Down) move the selection. Refs let the keyboard handler shift
  // DOM focus to the newly-selected radio so SR + sighted focus agree.
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectIndex = (index: number) => {
    const opt = options[index];
    if (!opt) return;
    onChange(opt.value);
    itemRefs.current[index]?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (options.length === 0) return;
    let next = index;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % options.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + options.length) % options.length;
        break;
      default:
        return;
    }
    e.preventDefault();
    selectIndex(next);
  };

  // A radiogroup must always have exactly one tab stop. If `value` matches
  // no option, fall back to making the first option tabbable so the group
  // stays keyboard-reachable.
  const selectedIndex = options.findIndex((o) => o.value === value);
  const tabbableIndex = selectedIndex === -1 ? 0 : selectedIndex;

  return (
    <div className="seg" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o, i) => {
        const checked = value === o.value;
        return (
          <button
            key={o.value}
            ref={(el) => {
              itemRefs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={i === tabbableIndex ? 0 : -1}
            className="seg-item"
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => handleKeyDown(e, i)}
          >
            {o.icon}
            <span>{o.label}</span>
          </button>
        );
      })}
    </div>
  );
}
